/* Banking Partner Portal — Overview, Transactions, Webhooks, Chargebacks,
   and API Health pages (Phase PP-1 · Feb 2026).

   Each export below is a separate route. The shared portal shell is
   `BankingPortalShell.jsx`. All endpoints are scope-gated on the backend
   so we don't need extra guard logic in the UI.
*/
import { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  Activity, CreditCard, Banknote, Zap, LineChart, FileWarning,
  AlertTriangle, ShieldCheck, RefreshCw, Webhook, Send, Building2, Clock,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import { TrustPostureHero } from "./TrustPostureHero";

// ─── Shared inline helpers ───────────────────────────────────────────────
function KpiTile({ label, value, sub, tone = "slate", icon: Icon, testid }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-xl border bg-${tone}-50 border-${tone}-200 px-4 py-3`}
    >
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
        {Icon && <Icon className={`w-4 h-4 text-${tone}-500`} />}
      </div>
      <div className="text-2xl font-semibold tabular-nums text-slate-900 mt-1.5">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function fmtMoney(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(n);
}

function fmtTime(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleString(); } catch { return s; }
}

const RAIL_TONES = {
  card: "violet", credit_card: "violet", debit_card: "violet",
  ach: "sky", rtp: "amber", wire: "emerald", ledger: "slate", tabapay: "violet",
};

// ════════════════════════════════════════════════════════════════════════
// OVERVIEW
// ════════════════════════════════════════════════════════════════════════
export function BankingOverview() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/partner/banking/overview", { params: { since_days: days } });
      setData(data);
    } catch (e) {
      toast.error("Could not load overview");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  return (
    <div data-testid="banking-overview" className="space-y-5">
      {/* ── Wave-12 · Trust Posture hero (NIST CSF) ───────────────── */}
      <TrustPostureHero />

      {/* ── Operational health (existing rails KPIs) ──────────────── */}
      <div className="pt-2 border-t border-slate-200">
        <header className="flex items-center justify-between mt-3">
          <div>
            <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <Activity className="w-4 h-4 text-sky-500" /> Operational Health
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Live rail volume + failures + webhook + API health for the period below.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={String(days)} onValueChange={v => setDays(Number(v))}>
              <SelectTrigger data-testid="overview-window" className="w-32 h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Last 24h</SelectItem>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={load} disabled={loading} data-testid="overview-refresh">
              <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>
        </header>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="overview-kpis">
            <KpiTile
              testid="overview-kpi-txn-count"
              label="Transactions"
              value={data.transactions.total.toLocaleString()}
              sub={`since ${days}d`}
              icon={Activity}
              tone="sky"
            />
            <KpiTile
              testid="overview-kpi-volume"
              label="Volume"
              value={fmtMoney(data.transactions.volume)}
              sub="absolute · all rails"
              icon={Banknote}
              tone="emerald"
            />
            <KpiTile
              testid="overview-kpi-failures"
              label="Failures"
              value={data.transactions.failures.toLocaleString()}
              icon={AlertTriangle}
              tone="rose"
            />
            <KpiTile
              testid="overview-kpi-webhooks"
              label="Webhook events 24h"
              value={data.webhooks_24h.events.toLocaleString()}
              sub={`${data.webhooks_24h.bad_sig} bad sig · ${data.webhooks_24h.unapplied} unapplied`}
              icon={Webhook}
              tone="amber"
            />
            <KpiTile
              testid="overview-kpi-api"
              label="API calls 24h"
              value={data.api_24h.calls.toLocaleString()}
              sub={`${data.api_24h.error_pct}% error rate`}
              icon={LineChart}
              tone={data.api_24h.error_pct > 5 ? "rose" : "slate"}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-2xl border bg-white p-4" data-testid="overview-by-rail">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                  <CreditCard className="w-4 h-4" /> Volume by rail
                </div>
                <div className="text-xs text-slate-500">Last {days} days</div>
              </div>
              <div className="space-y-2">
                {Object.keys(data.transactions.by_rail).length === 0 && (
                  <div className="text-sm text-slate-500">No transactions in window.</div>
                )}
                {Object.entries(data.transactions.by_rail).map(([rail, n]) => {
                  const tone = RAIL_TONES[rail] || "slate";
                  return (
                    <div key={rail} className="flex items-center justify-between text-sm">
                      <Badge variant="outline" className={`bg-${tone}-50 text-${tone}-700 border-${tone}-200 uppercase text-[10px] tracking-wider`}>
                        {rail}
                      </Badge>
                      <div className="tabular-nums text-slate-700">{n.toLocaleString()} txns</div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="rounded-2xl border bg-white p-4" data-testid="overview-chargebacks">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                  <FileWarning className="w-4 h-4" /> Chargebacks
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <KpiTile testid="cb-kpi-open" label="Open" value={data.chargebacks.open} tone="rose" />
                <KpiTile testid="cb-kpi-30d" label="Total 30d" value={data.chargebacks.total_30d} tone="slate" />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════
// TRANSACTIONS
// ════════════════════════════════════════════════════════════════════════
export function BankingTransactions() {
  const [rows, setRows] = useState([]);
  const [rail, setRail] = useState("all");
  const [status, setStatus] = useState("all");
  const [days, setDays] = useState(7);
  const [open, setOpen] = useState(null);

  const load = async () => {
    const params = { since_days: days, limit: 200 };
    if (rail !== "all") params.rail = rail;
    if (status !== "all") params.status = status;
    const { data } = await api.get("/partner/banking/transactions", { params });
    setRows(data.rows);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [rail, status, days]);

  return (
    <div data-testid="banking-transactions" className="space-y-4">
      <header className="flex items-center justify-between">
        <h1 className="display text-2xl tracking-tight flex items-center gap-2"><Activity className="w-5 h-5" /> Transactions</h1>
      </header>

      <div className="flex flex-wrap gap-2 items-center rounded-2xl border bg-white px-4 py-3">
        <Select value={rail} onValueChange={setRail}>
          <SelectTrigger data-testid="txn-filter-rail" className="w-32 h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All rails</SelectItem>
            <SelectItem value="card">Card</SelectItem>
            <SelectItem value="ach">ACH</SelectItem>
            <SelectItem value="rtp">RTP</SelectItem>
            <SelectItem value="wire">Wire</SelectItem>
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="txn-filter-status" className="w-36 h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="returned">Returned</SelectItem>
          </SelectContent>
        </Select>
        <Select value={String(days)} onValueChange={v => setDays(Number(v))}>
          <SelectTrigger data-testid="txn-filter-since" className="w-32 h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1">24h</SelectItem>
            <SelectItem value="7">7 days</SelectItem>
            <SelectItem value="30">30 days</SelectItem>
            <SelectItem value="90">90 days</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto text-xs text-slate-500" data-testid="txn-total">{rows.length} rows</div>
      </div>

      <div className="rounded-2xl border bg-white overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-2 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 border-b">
          <div className="col-span-2">When</div>
          <div className="col-span-1">Rail</div>
          <div className="col-span-2">User ref</div>
          <div className="col-span-2">Amount</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-3">TabaPay txn</div>
        </div>
        <div data-testid="txn-table">
          {rows.length === 0 && (
            <div className="text-center text-sm text-slate-500 py-10" data-testid="txn-empty">
              No transactions in window.
            </div>
          )}
          {rows.map(r => {
            const tone = RAIL_TONES[r.method] || "slate";
            return (
              <button
                key={r.id}
                onClick={() => setOpen(r)}
                data-testid={`txn-row-${r.id}`}
                className="w-full text-left grid grid-cols-12 px-4 py-2.5 border-b border-slate-100 hover:bg-slate-50/70 text-sm"
              >
                <div className="col-span-2 text-xs text-slate-500">{fmtTime(r.created_at)}</div>
                <div className="col-span-1"><Badge variant="outline" className={`bg-${tone}-50 text-${tone}-700 border-${tone}-200 text-[10px]`}>{r.method || "ledger"}</Badge></div>
                <div className="col-span-2 font-mono text-xs text-slate-600">{r.user_ref || "—"}</div>
                <div className="col-span-2 tabular-nums">{fmtMoney(r.amount)}</div>
                <div className="col-span-2"><span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{r.status}</span></div>
                <div className="col-span-3 font-mono text-[11px] text-slate-500 truncate">{r.tabapay_transaction_id || r.provider_txn_id || "—"}</div>
              </button>
            );
          })}
        </div>
      </div>

      <TxnDetailDialog txn={open} onClose={() => setOpen(null)} />
    </div>
  );
}


function TxnDetailDialog({ txn, onClose }) {
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    if (!txn) { setDetail(null); return; }
    api.get(`/partner/banking/transactions/${txn.id}`).then(r => setDetail(r.data)).catch(() => {});
  }, [txn?.id]);
  if (!txn) return null;
  return (
    <Dialog open={!!txn} onOpenChange={onClose}>
      <DialogContent data-testid="txn-detail-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Transaction · {txn.id.slice(0, 8)}…</DialogTitle>
          <DialogDescription>{txn.method?.toUpperCase()} · {fmtMoney(txn.amount)} · {txn.status}</DialogDescription>
        </DialogHeader>
        {detail && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="rounded-lg border bg-slate-50 px-3 py-2"><div className="text-[10px] uppercase text-slate-500">Created</div>{fmtTime(detail.txn.created_at)}</div>
              <div className="rounded-lg border bg-slate-50 px-3 py-2"><div className="text-[10px] uppercase text-slate-500">TabaPay txn</div><span className="font-mono text-xs">{detail.txn.tabapay_transaction_id || "—"}</span></div>
              <div className="rounded-lg border bg-slate-50 px-3 py-2"><div className="text-[10px] uppercase text-slate-500">User ref</div><span className="font-mono text-xs">{detail.txn.user_ref || "—"}</span></div>
              <div className="rounded-lg border bg-slate-50 px-3 py-2"><div className="text-[10px] uppercase text-slate-500">Memo</div>{detail.txn.memo || detail.txn.description || "—"}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-slate-700 mb-2">Linked webhook events ({detail.webhook_events.length})</div>
              <div className="rounded-lg border divide-y" data-testid="txn-webhook-events">
                {detail.webhook_events.length === 0 && <div className="text-sm text-slate-500 px-3 py-3">No webhook events linked.</div>}
                {detail.webhook_events.map(e => (
                  <div key={e.id || e.event_id} className="px-3 py-2 text-sm flex items-center gap-2">
                    <Clock className="w-3.5 h-3.5 text-slate-400" />
                    <span className="text-xs text-slate-500">{fmtTime(e.received_at)}</span>
                    <span className="font-mono text-xs">{e.event_type || e.raw?.eventType}</span>
                    {e.signature_ok === false && <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 text-[10px]">bad sig</Badge>}
                    {e.applied === false && <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">unapplied</Badge>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


// ════════════════════════════════════════════════════════════════════════
// WEBHOOKS
// ════════════════════════════════════════════════════════════════════════
export function BankingWebhooks() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ events: 0, bad_sig: 0, unapplied: 0 });
  const [onlyFailed, setOnlyFailed] = useState(false);
  const [onlyBadSig, setOnlyBadSig] = useState(false);
  const [days, setDays] = useState(7);

  const load = async () => {
    const { data } = await api.get("/partner/banking/webhooks", {
      params: { only_failed: onlyFailed, only_bad_sig: onlyBadSig, since_days: days, limit: 200 },
    });
    setRows(data.rows);
    setStats(data.stats);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [onlyFailed, onlyBadSig, days]);

  return (
    <div data-testid="banking-webhooks" className="space-y-4">
      <h1 className="display text-2xl tracking-tight flex items-center gap-2"><Webhook className="w-5 h-5" /> Webhooks</h1>

      <div className="grid grid-cols-3 gap-3" data-testid="wh-kpis">
        <KpiTile testid="wh-kpi-events"     label="Events"     value={stats.events.toLocaleString()}    icon={Webhook}        tone="slate" />
        <KpiTile testid="wh-kpi-bad-sig"    label="Bad signature" value={stats.bad_sig.toLocaleString()}    icon={AlertTriangle}  tone="rose" />
        <KpiTile testid="wh-kpi-unapplied"  label="Unapplied"   value={stats.unapplied.toLocaleString()}  icon={Clock}          tone="amber" />
      </div>

      <div className="flex flex-wrap gap-2 items-center rounded-2xl border bg-white px-4 py-3">
        <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={onlyFailed} onChange={e => setOnlyFailed(e.target.checked)} data-testid="wh-filter-failed" /> Only unapplied</label>
        <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={onlyBadSig} onChange={e => setOnlyBadSig(e.target.checked)} data-testid="wh-filter-badsig" /> Only bad signature</label>
        <Select value={String(days)} onValueChange={v => setDays(Number(v))}>
          <SelectTrigger data-testid="wh-filter-since" className="w-32 h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1">24h</SelectItem>
            <SelectItem value="7">7 days</SelectItem>
            <SelectItem value="30">30 days</SelectItem>
            <SelectItem value="90">90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-2xl border bg-white">
        <div className="grid grid-cols-12 px-4 py-2 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 border-b">
          <div className="col-span-3">When</div>
          <div className="col-span-3">Type</div>
          <div className="col-span-2">Signature</div>
          <div className="col-span-2">Applied</div>
          <div className="col-span-2">Effect</div>
        </div>
        <div data-testid="wh-table">
          {rows.length === 0 && (
            <div className="text-center text-sm text-slate-500 py-10" data-testid="wh-empty">No webhook events.</div>
          )}
          {rows.map(r => (
            <div key={r.id || r.event_id} className="grid grid-cols-12 px-4 py-2 border-b text-sm">
              <div className="col-span-3 text-xs text-slate-500">{fmtTime(r.received_at)}</div>
              <div className="col-span-3 font-mono text-xs">{r.event_type || r.raw?.eventType || "—"}</div>
              <div className="col-span-2">{r.signature_ok ? <span className="text-emerald-600 text-xs">OK</span> : <span className="text-rose-600 text-xs">FAIL</span>}</div>
              <div className="col-span-2">{r.applied ? <span className="text-emerald-600 text-xs">Yes</span> : <span className="text-slate-500 text-xs">No</span>}</div>
              <div className="col-span-2 text-xs text-slate-500 truncate">{r.effect || "—"}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════
// CHARGEBACKS
// ════════════════════════════════════════════════════════════════════════
export function BankingChargebacks() {
  const [rows, setRows] = useState([]);
  const [byStatus, setByStatus] = useState({});
  const [status, setStatus] = useState("all");
  const [days, setDays] = useState(60);
  const [open, setOpen] = useState(null);

  const load = async () => {
    const params = { since_days: days };
    if (status !== "all") params.status = status;
    const { data } = await api.get("/partner/banking/chargebacks", { params });
    setRows(data.rows);
    setByStatus(data.by_status || {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, days]);

  return (
    <div data-testid="banking-chargebacks" className="space-y-4">
      <h1 className="display text-2xl tracking-tight flex items-center gap-2"><FileWarning className="w-5 h-5" /> Chargebacks</h1>

      <div className="grid grid-cols-5 gap-3" data-testid="cb-status-strip">
        {["open", "responded", "won", "lost", "expired"].map(s => (
          <KpiTile key={s} testid={`cb-strip-${s}`} label={s} value={byStatus[s] || 0} tone={s === "open" ? "rose" : s === "won" ? "emerald" : s === "lost" ? "amber" : "slate"} />
        ))}
      </div>

      <div className="flex gap-2 items-center rounded-2xl border bg-white px-4 py-3">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="cb-filter-status" className="w-36 h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            {["open", "responded", "won", "lost", "expired"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={String(days)} onValueChange={v => setDays(Number(v))}>
          <SelectTrigger data-testid="cb-filter-since" className="w-32 h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="30">30 days</SelectItem>
            <SelectItem value="60">60 days</SelectItem>
            <SelectItem value="90">90 days</SelectItem>
            <SelectItem value="180">180 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-2xl border bg-white">
        <div className="grid grid-cols-12 px-4 py-2 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 border-b">
          <div className="col-span-3">Opened</div>
          <div className="col-span-2">Amount</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-3">Reason</div>
          <div className="col-span-2 text-right">Action</div>
        </div>
        <div data-testid="cb-table">
          {rows.length === 0 && <div className="text-center text-sm text-slate-500 py-10" data-testid="cb-empty">No chargebacks in window.</div>}
          {rows.map(r => (
            <div key={r.id} data-testid={`cb-row-${r.id}`} className="grid grid-cols-12 px-4 py-2 border-b text-sm items-center">
              <div className="col-span-3 text-xs text-slate-500">{fmtTime(r.opened_at)}</div>
              <div className="col-span-2 tabular-nums">{fmtMoney(r.amount)}</div>
              <div className="col-span-2"><span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{r.status}</span></div>
              <div className="col-span-3 text-xs text-slate-600 truncate">{r.reason || "—"}</div>
              <div className="col-span-2 text-right">
                {r.status === "open" && (
                  <Button size="sm" variant="outline" onClick={() => setOpen(r)} data-testid={`cb-respond-${r.id}`}>
                    <Send className="w-3.5 h-3.5 mr-1" /> Respond
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <CbRespondDialog cb={open} onClose={() => setOpen(null)} onDone={load} />
    </div>
  );
}

function CbRespondDialog({ cb, onClose, onDone }) {
  const [evidence, setEvidence] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (cb) { setEvidence(""); setReason(""); } }, [cb?.id]);
  if (!cb) return null;

  const submit = async () => {
    if (evidence.trim().length < 3 || reason.trim().length < 3) return;
    setBusy(true);
    try {
      await api.patch(`/partner/banking/chargebacks/${cb.id}`, {
        evidence_note: evidence, reason,
      });
      toast.success("Evidence submitted");
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Submit failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!cb} onOpenChange={onClose}>
      <DialogContent data-testid="cb-respond-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Respond to chargeback</DialogTitle>
          <DialogDescription>Submits evidence and moves the case to "responded" status.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Evidence note</Label>
            <Textarea data-testid="cb-evidence" value={evidence} onChange={e => setEvidence(e.target.value)} rows={4} placeholder="Customer signed POD attached · receipts uploaded to portal" />
          </div>
          <div>
            <Label>Reason (audit)</Label>
            <Textarea data-testid="cb-respond-reason" value={reason} onChange={e => setReason(e.target.value)} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="cb-respond-submit" onClick={submit} disabled={busy || evidence.trim().length < 3 || reason.trim().length < 3}>
            {busy ? "Submitting…" : "Submit evidence"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ════════════════════════════════════════════════════════════════════════
// API HEALTH
// ════════════════════════════════════════════════════════════════════════
export function BankingApiHealth() {
  const [data, setData] = useState(null);
  const load = async () => {
    const { data } = await api.get("/partner/banking/api-health");
    setData(data);
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="banking-api-health" className="space-y-4">
      <header className="flex items-center justify-between">
        <h1 className="display text-2xl tracking-tight flex items-center gap-2"><LineChart className="w-5 h-5" /> API health</h1>
        <Button variant="outline" onClick={load} data-testid="health-refresh">
          <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
        </Button>
      </header>

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="health-kpis">
            <KpiTile testid="health-calls"   label="Calls 24h"   value={data.calls.toLocaleString()} icon={Activity}      tone="slate" />
            <KpiTile testid="health-errors"  label="Errors 24h"  value={data.errors.toLocaleString()} icon={AlertTriangle} tone={data.errors ? "rose" : "emerald"} />
            <KpiTile testid="health-errpct"  label="Error rate"  value={`${data.error_pct}%`} tone={data.error_pct > 5 ? "rose" : "emerald"} />
            <KpiTile testid="health-p95"     label="p95 latency" value={data.latency.p95_ms ? `${data.latency.p95_ms} ms` : "—"} sub={`p50: ${data.latency.p50_ms ?? "—"} ms · n=${data.latency.sampled}`} tone="slate" />
          </div>

          <div className="rounded-2xl border bg-white p-4">
            <div className="text-sm font-medium text-slate-700 mb-3">Recent errors</div>
            <div data-testid="health-errors-list">
              {data.recent_errors.length === 0 ? (
                <div className="text-sm text-slate-500 py-4 flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-500" /> No errors in last 24h.</div>
              ) : data.recent_errors.map((e, i) => (
                <div key={i} className="text-sm border-b py-2 grid grid-cols-12 gap-3">
                  <div className="col-span-3 text-xs text-slate-500">{fmtTime(e.at)}</div>
                  <div className="col-span-2 font-mono text-xs">{e.method} {e.status_code}</div>
                  <div className="col-span-3 font-mono text-xs truncate">{e.endpoint}</div>
                  <div className="col-span-4 text-xs text-rose-700 truncate">{e.error}</div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
